import type { FC, ReactNode } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { ConsumptionDetails } from '@eneco/flows/components';
import { DetailsType } from '@eneco/flows/types';
import { useFormatter } from '@eneco/i18n';
import { Box, Stack, Text, TextLink } from '@eneco/sparky';
import { SensitiveContentComponent } from '@eneco/tracking';
import { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

interface Props {
  theme?: DC_Repositories_Base_Enumerations_Label;
  textualData: {
    redeliveryLabel?: string;
    electricityLabel?: string;
    electricityHighLabel?: string;
    electricityLowLabel?: string;
    gasLabel?: string;
    warmthLabel?: string;
    waterLabel?: string;
    description?: string;
  };
  data: {
    hasDoubleMeter?: boolean;
    hasSolarPanels?: boolean;

    usageElectricityHigh?: number;
    usageElectricityLow?: number;
    usageGas?: number;
    usageWarmth?: number;
    usageWater?: number;

    solarPanelsOutput?: number;
  };
  editButton?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  layout?: string;
}

interface ConsumptionDetailsListProps {
  theme?: DC_Repositories_Base_Enumerations_Label;
  labels: Omit<Props['textualData'], 'description'>;
  data: Props['data'];
}

/** The `ConsumptionDetails` items rendered by both `OverviewConsumption` layouts. */
const ConsumptionDetailsList: FC<ConsumptionDetailsListProps> = ({
  theme,
  labels: {
    electricityLabel,
    electricityHighLabel,
    electricityLowLabel,
    gasLabel,
    warmthLabel,
    waterLabel,
    redeliveryLabel,
  },
  data: {
    hasDoubleMeter,
    hasSolarPanels,
    usageElectricityHigh,
    usageElectricityLow,
    usageGas,
    usageWarmth,
    usageWater,
    solarPanelsOutput,
  },
}): ReactNode => {
  const { unit } = useFormatter();
  const labelPrefix = (label?: string) => (label ? `${label}: ` : '');

  return (
    <>
      {usageElectricityHigh != null && (
        <ConsumptionDetails theme={theme} type={DetailsType.Electricity} title={electricityLabel}>
          {hasDoubleMeter
            ? `${labelPrefix(electricityHighLabel)}${unit.electricity(usageElectricityHigh)}
                   ${labelPrefix(electricityLowLabel)}${unit.electricity(usageElectricityLow ?? 0)}`
            : ` ${unit.electricity(usageElectricityHigh)}`}
        </ConsumptionDetails>
      )}

      {usageGas != null && (
        <ConsumptionDetails theme={theme} type={DetailsType.Gas} title={gasLabel}>
          {unit.gas(usageGas)}
        </ConsumptionDetails>
      )}

      {usageWarmth != null && (
        <ConsumptionDetails theme={theme} type={DetailsType.Warmth} title={warmthLabel}>
          {unit.warmth(usageWarmth)}
        </ConsumptionDetails>
      )}

      {usageWater != null && (
        <ConsumptionDetails theme={theme} type={DetailsType.Water} title={waterLabel}>
          {unit.tapWater(usageWater)}
        </ConsumptionDetails>
      )}

      {hasSolarPanels && !!solarPanelsOutput && (
        <ConsumptionDetails theme={theme} type={DetailsType.Redelivery} title={redeliveryLabel}>
          {unit.redelivery(solarPanelsOutput)}
        </ConsumptionDetails>
      )}
    </>
  );
};

export const OverviewConsumption: FC<Props> = ({
  theme,
  textualData: {
    description,
    redeliveryLabel,
    electricityLabel,
    electricityHighLabel,
    electricityLowLabel,
    gasLabel,
    warmthLabel,
    waterLabel,
  },
  data: {
    hasDoubleMeter,
    hasSolarPanels,

    usageElectricityHigh,
    usageElectricityLow,
    usageGas,
    usageWarmth,
    usageWater,

    solarPanelsOutput,
  },
  layout,
  editButton,
}) => {
  const labels = {
    electricityLabel,
    electricityHighLabel,
    electricityLowLabel,
    gasLabel,
    warmthLabel,
    waterLabel,
    redeliveryLabel,
  };
  const data = {
    hasDoubleMeter,
    hasSolarPanels,
    usageElectricityHigh,
    usageElectricityLow,
    usageGas,
    usageWarmth,
    usageWater,
    solarPanelsOutput,
  };

  if (layout === 'B') {
    return (
      <Stack direction={{ initial: 'column', md: 'row' }} alignY="center" alignX="start" wrap gap="2">
        <Text as="div" size="BodyXS">
          <SensitiveContentComponent contentMask="masked address">
            <RichText html={description} />
          </SensitiveContentComponent>
        </Text>

        <ConsumptionDetailsList theme={theme} labels={labels} data={data} />

        {editButton && (
          <Text size="BodyXS">
            <TextLink href={editButton.href} onClick={editButton.onClick}>
              {editButton.label}
            </TextLink>
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Box paddingTop="4" paddingBottom="10">
      <Stack direction="column" alignX={{ initial: 'start', md: 'center' }} gap="4">
        <Text size="BodyXS">
          <RichText html={description} />
        </Text>

        <Stack direction={{ initial: 'column', md: 'row' }} alignX={{ initial: 'start', md: 'center' }} gap="2" wrap>
          <ConsumptionDetailsList theme={theme} labels={labels} data={data} />
        </Stack>

        {editButton && (
          <Text size="BodyXS">
            <TextLink href={editButton.href} onClick={editButton.onClick}>
              {editButton.label}
            </TextLink>
          </Text>
        )}
      </Stack>
    </Box>
  );
};
